import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight } from '@fortawesome/sharp-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { DivButton } from '@/components/divButton';
import Typography from '@/components/typography';
import { useAuth } from '@/providers/auth-provider';

import { ToolMapper, ToolMenu, ToolMenuContent } from './constants';
import { useDisableToolItem } from './hooks/useDisableToolItem';
import { useToolbarContext } from './provider';
import { ToolItem } from './toolItem';

interface Props {}

export const Tools: React.FC<Props> = () => {
  const { userCanAccessDeedPlotter } = useAuth();
  const [openedToolMenu, setOpenedToolMenu] = useState<ToolMenu | null>(null);
  const { isToolsOpen, setIsToolsOpen } = useToolbarContext();

  const disabledLogicMapper = useDisableToolItem();

  const [menuIcons, setMenuIcons] = useState<
    Record<ToolMenu, ToolMenuContent | undefined>
  >(
    Object.keys(ToolMapper).reduce((acc, tool) => {
      // eslint-disable-next-line no-param-reassign
      acc[tool] =
        ToolMapper[tool]?.menu?.find(
          (menuItem) => menuItem.showAsDefaultIcon
        ) ?? undefined;
      return acc;
    }, {} as Record<ToolMenu, ToolMenuContent | undefined>)
  );

  useEffect(() => {
    if (!isToolsOpen) {
      setOpenedToolMenu(null);
    }
  }, [isToolsOpen]);

  const toolItems = React.useMemo(() => {
    const filterOutForbiddenDeed = (toolMode: ToolMenu) =>
      !(toolMode === ToolMenu.Deed && !userCanAccessDeedPlotter);

    return Object.values(ToolMenu)
      .filter(filterOutForbiddenDeed)
      .map((toolMode) => (
        <ToolItem
          key={toolMode}
          disabled={disabledLogicMapper[toolMode]}
          isToolsOpen={isToolsOpen}
          item={toolMode}
          menuIcon={menuIcons[toolMode]}
          openedToolMenu={openedToolMenu}
          setMenuIcon={setMenuIcons}
          setOpenedToolMenu={setOpenedToolMenu}
        />
      ));
  }, [
    disabledLogicMapper,
    isToolsOpen,
    menuIcons,
    openedToolMenu,
    userCanAccessDeedPlotter,
  ]);

  return (
    <div className="pointer-events-auto flex h-10 items-center justify-center">
      <DivButton
        className="flex h-full items-center justify-center gap-x-3 rounded-l-sm bg-gray-100 px-3"
        onClick={() => setIsToolsOpen(!isToolsOpen)}
      >
        <Typography className="text-white" variant="button2">
          Tools
        </Typography>
      </DivButton>

      <div className="flex h-full w-full items-center justify-center bg-gray-90 text-white">
        {toolItems}
      </div>

      <DivButton
        className="flex h-full items-center justify-center gap-x-3 rounded-r-sm bg-gray-100 px-3"
        data-tooltip-content={isToolsOpen ? 'Close' : 'Open'}
        data-tooltip-place="right"
        onClick={() => setIsToolsOpen(!isToolsOpen)}
      >
        <FontAwesomeIcon
          className={twMerge(
            'h-[12px] text-white transition-transform duration-500',
            isToolsOpen ? 'rotate-180' : 'rotate-0'
          )}
          icon={faCaretRight}
        />
      </DivButton>
    </div>
  );
};
